import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Button, IconButton, Typography } from '@mui/material';
import {
  Close as CloseIcon,
  ErrorOutline,
  WarningAmber,
  ScheduleOutlined,
  BlockOutlined,
} from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import { STATUS_BG_COLORS, STATUS_FG_COLORS, NEUTRAL_COLORS } from '@/theme/colorTokens';
import { billingApi, SubscriptionWithUsage, UsageWarning, UsageWarningsResponse } from '@/api/endpoints/billing';
import { isDunning } from '@/constants/subscriptionStatus';

/**
 * SystemAlertBar — single persistent strip shown above page content.
 *
 * Priority (highest wins; only one renders):
 *   P0  past_due         → payment failed, service at risk
 *   P1  exceeded usage   → hard plan limit hit (non-dismissible)
 *   P2  trialing ≤3 days → trial critical (non-dismissible)
 *   P3  critical usage   → 90%+ of a resource
 *   P4  trialing >3 days → trial informational (dismissible)
 *   P5  warning usage    → 70%+ of a resource (dismissible)
 *
 * Replaces the three separate TrialBanner / DunningBanner / UsageLimitAlert
 * mounts, which could stack when e.g. a past_due subscription also has a
 * plan-limit exceeded. All data comes from two shared React Query caches
 * so no extra network requests are made.
 */

interface Props {
  onUpgrade: () => void;
}

function daysRemaining(trialEndsAt: string | null): number | null {
  if (!trialEndsAt) return null;
  const diffMs = new Date(trialEndsAt).getTime() - Date.now();
  if (Number.isNaN(diffMs)) return null;
  if (diffMs <= 0) return 0;
  return Math.ceil(diffMs / 86_400_000);
}

const TRIAL_DISMISS_KEY = 'trialBanner:dismissedFor';
const USAGE_DISMISS_PREFIX = 'usageAlert:dismissed:';

type AlertSpec = {
  priority: number;
  bg: string;
  fg: string;
  Icon: React.ElementType;
  message: string;
  ctaLabel: string;
  onCta: () => void;
  dismissible: boolean;
  dismissKey: string;
  ariaLive: 'polite' | 'assertive';
};

export default function SystemAlertBar({ onUpgrade }: Props) {
  const [localDismissed, setLocalDismissed] = useState<string | null>(null);

  const { data: sub } = useQuery<SubscriptionWithUsage>({
    queryKey: ['billing', 'subscription'],
    queryFn: () => billingApi.getSubscriptionForBanner().then((r) => r.data),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const { data: usageData } = useQuery<UsageWarningsResponse>({
    queryKey: ['billing', 'usage-warnings'],
    queryFn: () => billingApi.getUsageWarnings().then((r) => r.data),
    staleTime: 2 * 60 * 1000,
    retry: false,
  });

  const openPortal = () =>
    billingApi.openPortal().then((r) => window.open(r.data.portal_url)).catch(() => {});

  const topWarning = useMemo<UsageWarning | null>(() => {
    if (!usageData?.warnings?.length) return null;
    const order = { exceeded: 3, critical: 2, warning: 1 } as const;
    return [...usageData.warnings].sort((a, b) => order[b.severity] - order[a.severity])[0];
  }, [usageData]);

  const trialDays = useMemo(
    () => (sub?.status === 'trialing' ? daysRemaining(sub.trial_ends_at ?? null) : null),
    [sub],
  );

  // Build candidate alerts in priority order; first non-dismissed wins.
  const candidates = useMemo<AlertSpec[]>(() => {
    const specs: AlertSpec[] = [];

    // P0 — dunning (past_due, grace_period, account_hold)
    if (sub && isDunning(sub.status)) {
      specs.push({
        priority: 0,
        bg: STATUS_BG_COLORS.error,
        fg: STATUS_FG_COLORS.error,
        Icon: ErrorOutline,
        message: `Your last payment for ${sub.plan_name} failed. Update your card to avoid service interruption.`,
        ctaLabel: 'Update payment method',
        onCta: openPortal,
        dismissible: false,
        dismissKey: 'dunning',
        ariaLive: 'assertive',
      });
    }

    // P1 — exceeded usage (non-dismissible hard block)
    if (topWarning?.severity === 'exceeded') {
      specs.push({
        priority: 1,
        bg: STATUS_BG_COLORS.error,
        fg: STATUS_FG_COLORS.error,
        Icon: BlockOutlined,
        message: topWarning.message,
        ctaLabel: 'Upgrade to unblock',
        onCta: onUpgrade,
        dismissible: false,
        dismissKey: `usage:exceeded:${topWarning.resource}`,
        ariaLive: 'assertive',
      });
    }

    // P2 — trialing ≤3 days (non-dismissible)
    if (sub?.status === 'trialing' && trialDays !== null && trialDays <= 3) {
      const label =
        trialDays === 0
          ? `Your ${sub.plan_name} trial ends today`
          : trialDays === 1
            ? `Your ${sub.plan_name} trial ends tomorrow`
            : `${trialDays} days left in your ${sub.plan_name} trial`;
      specs.push({
        priority: 2,
        bg: STATUS_BG_COLORS.error,
        fg: STATUS_FG_COLORS.error,
        Icon: ErrorOutline,
        message: label,
        ctaLabel: 'Upgrade now',
        onCta: onUpgrade,
        dismissible: false,
        dismissKey: `trial:critical:${sub.trial_ends_at}`,
        ariaLive: 'assertive',
      });
    }

    // P3 — critical usage (90%+, dismissible)
    if (topWarning?.severity === 'critical') {
      specs.push({
        priority: 3,
        bg: STATUS_BG_COLORS.warning,
        fg: STATUS_FG_COLORS.warningDeep,
        Icon: WarningAmber,
        message: topWarning.message,
        ctaLabel: 'Upgrade plan',
        onCta: onUpgrade,
        dismissible: true,
        dismissKey: `usage:critical:${topWarning.resource}:${topWarning.percent}`,
        ariaLive: 'polite',
      });
    }

    // P4 — trialing >3 days (dismissible per trial_ends_at)
    if (sub?.status === 'trialing' && trialDays !== null && trialDays > 3) {
      const label = `${trialDays} days left in your ${sub.plan_name} trial`;
      specs.push({
        priority: 4,
        bg: STATUS_BG_COLORS.info,
        fg: STATUS_FG_COLORS.info,
        Icon: ScheduleOutlined,
        message: label,
        ctaLabel: 'Upgrade now',
        onCta: onUpgrade,
        dismissible: true,
        dismissKey: `trial:info:${sub.trial_ends_at}`,
        ariaLive: 'polite',
      });
    }

    // P5 — warning usage (70–90%, dismissible)
    if (topWarning?.severity === 'warning') {
      specs.push({
        priority: 5,
        bg: STATUS_BG_COLORS.warningDeep,
        fg: STATUS_FG_COLORS.warningDeeper,
        Icon: WarningAmber,
        message: topWarning.message,
        ctaLabel: 'Upgrade plan',
        onCta: onUpgrade,
        dismissible: true,
        dismissKey: `usage:warning:${topWarning.resource}:${topWarning.percent}`,
        ariaLive: 'polite',
      });
    }

    return specs;
  }, [sub, topWarning, trialDays, onUpgrade]);

  // Walk candidates in priority order; return first non-dismissed.
  const active = useMemo<AlertSpec | null>(() => {
    for (const spec of candidates) {
      if (!spec.dismissible) return spec;
      const isDismissed =
        localDismissed === spec.dismissKey ||
        sessionStorage.getItem(
          spec.dismissKey.startsWith('usage:')
            ? `${USAGE_DISMISS_PREFIX}${spec.dismissKey}`
            : `${TRIAL_DISMISS_KEY}:${spec.dismissKey}`,
        ) === '1';
      if (!isDismissed) return spec;
    }
    return null;
  }, [candidates, localDismissed]);

  if (!active) return null;

  const Icon = active.Icon;

  const handleDismiss = () => {
    const storageKey = active.dismissKey.startsWith('usage:')
      ? `${USAGE_DISMISS_PREFIX}${active.dismissKey}`
      : `${TRIAL_DISMISS_KEY}:${active.dismissKey}`;
    sessionStorage.setItem(storageKey, '1');
    setLocalDismissed(active.dismissKey);
  };

  return (
    <Box
      role="alert"
      aria-live={active.ariaLive}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: { xs: 2, sm: 3 },
        py: 1,
        bgcolor: active.bg,
        color: active.fg,
        borderBottom: '1px solid',
        borderColor: `${active.fg}33`,
      }}
    >
      <Icon fontSize="small" />
      <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.medium, flex: 1 }}>
        {active.message}
      </Typography>
      <Button
        size="small"
        variant="contained"
        onClick={active.onCta}
        sx={{
          bgcolor: active.fg,
          color: NEUTRAL_COLORS.white,
          fontSize: TYPE.xs,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': { bgcolor: active.fg, opacity: 0.9, boxShadow: 'none' },
        }}
      >
        {active.ctaLabel}
      </Button>
      {active.dismissible && (
        <IconButton
          size="small"
          onClick={handleDismiss}
          aria-label="Dismiss alert"
          sx={{ color: active.fg }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}
